import React, { useState, useEffect } from "react";
import { Button, Tooltip, Drawer, List, ListItem, ListItemIcon, ListItemText, IconButton } from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import CloseIcon from "@material-ui/icons/Close";
import { useMobileDetection } from "../../utils/deviceDetection";
import GameRulesDialog from "./GameRulesDialog";
import CommonDialog from "../ui/CommonDialog";

/**
 * GameActionButtons
 * Displays game manual, tutorial, end turn, and quit game buttons
 * Also displays player stats: cash, net worth, and actions remaining
 */
const GameActionButtons = ({ myTurn, onEndTurn, onEndGame, onStartTutorial, playerName, cash = 0, netWorth = 0, actionsRemaining = 0, maxActionsPerTurn = 3 }) => {
  const [rulesOpen, setRulesOpen] = useState(false);
  const [quitConfirmOpen, setQuitConfirmOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useMobileDetection();

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleMenuAction = (action) => {
    setMenuOpen(false);
    if (action === 'rules') setRulesOpen(true);
    else if (action === 'tutorial') onStartTutorial();
    else if (action === 'endTurn') onEndTurn();
    else if (action === 'quit') setQuitConfirmOpen(true);
  };

  // Mobile: Show hamburger menu
  if (isMobile) {
    return (
      <>
        <Tooltip title="Menu" arrow placement="bottom">
          <IconButton
            onClick={() => setMenuOpen(true)}
            className="floating-action-button floating-action-button-menu"
            size="small"
          >
            <MenuIcon />
          </IconButton>
        </Tooltip>

        {/* Mobile Menu Drawer */}
        <Drawer
          anchor="bottom"
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          PaperProps={{
            style: {
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              maxHeight: '80vh',
              paddingBottom: 'env(safe-area-inset-bottom)'
            }
          }}
        >
          <div style={{ padding: '16px' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: '2px solid rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 'bold' }}>Game Actions</h3>
              <IconButton onClick={() => setMenuOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </div>
            
            <List>
              <ListItem 
                button 
                onClick={() => handleMenuAction('rules')}
                style={{ borderRadius: '8px', marginBottom: '4px' }}
              >
                <ListItemIcon style={{ minWidth: '40px', fontSize: '1.5rem' }}>📖</ListItemIcon>
                <ListItemText primary="Game Manual" secondary="View game rules" />
              </ListItem>
              
              <ListItem 
                button 
                onClick={() => handleMenuAction('tutorial')}
                style={{ borderRadius: '8px', marginBottom: '4px' }}
              >
                <ListItemIcon style={{ minWidth: '40px', fontSize: '1.5rem' }}>ℹ️</ListItemIcon>
                <ListItemText primary="Tutorial" secondary="Interactive guide" />
              </ListItem>
              
              <ListItem 
                button 
                onClick={() => handleMenuAction('endTurn')}
                disabled={!myTurn}
                style={{ 
                  borderRadius: '8px', 
                  marginBottom: '4px',
                  opacity: myTurn ? 1 : 0.5
                }}
              >
                <ListItemIcon style={{ minWidth: '40px', fontSize: '1.5rem' }}>✓</ListItemIcon>
                <ListItemText 
                  primary="End Turn" 
                  secondary={myTurn ? "End your turn" : "Not your turn"} 
                />
              </ListItem>
              
              <ListItem 
                button 
                onClick={() => handleMenuAction('quit')}
                style={{ borderRadius: '8px', marginBottom: '4px', color: '#dc3545' }}
              >
                <ListItemIcon style={{ minWidth: '40px', fontSize: '1.5rem' }}>🚪</ListItemIcon>
                <ListItemText primary="Quit Game" secondary="Leave the game" />
              </ListItem>
            </List>

            {/* Player Stats in Menu */}
            <div style={{
              marginTop: '16px',
              padding: '12px',
              background: 'rgba(21, 128, 61, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(21, 128, 61, 0.2)'
            }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '8px', color: '#15803d' }}>
                Your Stats
              </div>
              <div style={{ fontSize: '0.75rem', color: '#666', lineHeight: '1.6' }}>
                <div>💰 Cash: {formatCurrency(cash)}</div>
                <div>💎 Net Worth: {formatCurrency(netWorth)}</div>
                <div>⚡ Actions: {actionsRemaining}/{maxActionsPerTurn}</div>
              </div>
            </div>
          </div>
        </Drawer>

        {/* Dialogs - shared between mobile and desktop */}
        <CommonDialog
          open={quitConfirmOpen}
          onClose={() => setQuitConfirmOpen(false)}
          title="🚪 Quit Game"
          maxWidth={false}
          fullWidth={false}
          PaperProps={{
            className: 'quit-confirm-dialog',
            style: {
              border: '2px solid rgba(21, 128, 61, 0.2)',
              width: isMobile ? '90vw' : '320px',
              maxWidth: isMobile ? '90vw' : '320px',
              margin: 'auto'
            }
          }}
          titleStyle={{
            padding: '16px 20px',
            paddingRight: '48px',
            background: 'linear-gradient(135deg, rgba(21, 128, 61, 0.1) 0%, rgba(20, 83, 45, 0.05) 100%)'
          }}
          contentStyle={{
            padding: '24px 20px',
            overflowWrap: 'break-word',
            wordWrap: 'break-word'
          }}
          actions={
            <>
            <Button
              onClick={() => setQuitConfirmOpen(false)}
              style={{
                color: '#666',
                fontWeight: 500,
                textTransform: 'none',
                padding: '8px 16px'
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setQuitConfirmOpen(false);
                onEndGame();
              }}
              variant="contained"
              style={{
                background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                padding: '8px 20px',
                boxShadow: '0 2px 8px rgba(220, 53, 69, 0.3)'
              }}
            >
              Quit Game
            </Button>
            </>
          }
          actionsStyle={{
            padding: '12px 20px',
            borderTop: '1px solid rgba(0, 0, 0, 0.1)',
            gap: '8px'
          }}
        >
          <p style={{ 
            margin: 0, 
            fontSize: '1rem', 
            color: '#333',
            lineHeight: '1.6',
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}>
            Are you sure you want to leave the game?
          </p>
          <p style={{ 
            margin: '12px 0 0 0', 
            fontSize: '0.9rem', 
            color: '#666',
            lineHeight: '1.5',
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}>
            {process.env.NODE_ENV === 'development' 
              ? 'If there are other players, you will be replaced by a bot. Otherwise, the game will end.'
              : 'If there are at least 2 other players, you will be replaced by a bot. Otherwise, the game will end.'}
          </p>
        </CommonDialog>

        <GameRulesDialog
          open={rulesOpen}
          onClose={() => setRulesOpen(false)}
        />
      </>
    );
  }

  // Desktop: Show all buttons
  return (
    <>
      <Tooltip title="Game Manual" arrow placement="bottom">
        <IconButton
          onClick={() => setRulesOpen(true)}
          className="floating-action-button floating-action-button-manual"
          size="small"
        >
          📖
        </IconButton>
      </Tooltip>
      <Tooltip title="Interactive Tutorial" arrow placement="bottom">
        <IconButton
          onClick={onStartTutorial}
          className="floating-action-button floating-action-button-tutorial"
          size="small"
        >
          ℹ️
        </IconButton>
      </Tooltip>
      <Tooltip title={myTurn ? "End Turn" : "Not your turn"} arrow placement="bottom">
        <span>
          <IconButton
          disabled={!myTurn}
          onClick={onEndTurn}
            className={`floating-action-button floating-action-button-turn ${myTurn ? 'active' : ''}`}
          size="small"
          >
            ✓
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Quit Game" arrow placement="bottom">
        <IconButton 
          onClick={() => setQuitConfirmOpen(true)}
          className="floating-action-button floating-action-button-end"
          size="small"
        >
          🚪
        </IconButton>
      </Tooltip>

      {/* Quit Confirmation Dialog */}
      <CommonDialog
        open={quitConfirmOpen}
        onClose={() => setQuitConfirmOpen(false)}
        title="🚪 Quit Game"
        maxWidth={false}
        fullWidth={false}
        PaperProps={{
          className: 'quit-confirm-dialog',
          style: {
            border: '2px solid rgba(21, 128, 61, 0.2)',
            width: '320px !important',
            maxWidth: '320px !important',
            margin: 'auto'
          }
        }}
        titleStyle={{
          padding: '16px 20px',
          paddingRight: '48px',
          background: 'linear-gradient(135deg, rgba(21, 128, 61, 0.1) 0%, rgba(20, 83, 45, 0.05) 100%)'
        }}
        contentStyle={{
          padding: '24px 20px',
          overflowWrap: 'break-word',
          wordWrap: 'break-word'
        }}
        actions={
          <>
          <Button
            onClick={() => setQuitConfirmOpen(false)}
            style={{
              color: '#666',
              fontWeight: 500,
              textTransform: 'none',
              padding: '8px 16px'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setQuitConfirmOpen(false);
              onEndGame();
            }}
            variant="contained"
            style={{
              background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              padding: '8px 20px',
              boxShadow: '0 2px 8px rgba(220, 53, 69, 0.3)'
            }}
          >
            Quit Game
          </Button>
          </>
        }
        actionsStyle={{
          padding: '12px 20px',
          borderTop: '1px solid rgba(0, 0, 0, 0.1)',
          gap: '8px'
        }}
      >
        <p style={{ 
          margin: 0, 
          fontSize: '1rem', 
          color: '#333',
          lineHeight: '1.6',
          wordWrap: 'break-word',
          overflowWrap: 'break-word'
        }}>
          Are you sure you want to leave the game?
        </p>
        <p style={{ 
          margin: '12px 0 0 0', 
          fontSize: '0.9rem', 
          color: '#666',
          lineHeight: '1.5',
          wordWrap: 'break-word',
          overflowWrap: 'break-word'
        }}>
          {process.env.NODE_ENV === 'development' 
            ? 'If there are other players, you will be replaced by a bot. Otherwise, the game will end.'
            : 'If there are at least 2 other players, you will be replaced by a bot. Otherwise, the game will end.'}
        </p>
      </CommonDialog>

      {/* Game Rules Dialog */}
      <GameRulesDialog
        open={rulesOpen}
        onClose={() => setRulesOpen(false)}
      />
    </>
  );
};

export default GameActionButtons;

